import React, { useState, useEffect } from 'react';
import { eventService } from '../services/eventService';
import { FaCalendarAlt, FaClock, FaTrash, FaEdit } from 'react-icons/fa';

function Events() {
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', description: '' });
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventService.getAllEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const calculateTimeLeft = (eventDate) => {
    const now = new Date();
    const event = new Date(eventDate);
    const difference = event - now;

    if (difference < 0) return 'Event has passed';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return `${days} days ${hours} hours remaining`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await eventService.updateEvent(editingEvent._id, newEvent);
      } else {
        await eventService.createEvent(newEvent);
      }
      setNewEvent({ title: '', date: '', description: '' });
      setIsEditing(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleEdit = (event) => {
    setIsEditing(true);
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      date: new Date(event.date).toISOString().slice(0, 16), // Format for datetime-local input
      description: event.description
    });
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await eventService.deleteEvent(eventId);
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Events</h1>

      {/* Add/Edit Event Form */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {isEditing ? 'Edit Event' : 'Add New Event'}
              </h2>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditingEvent(null);
                    setNewEvent({ title: '', date: '', description: '' });
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancel Edit
                </button>
              )}
            </div>
            <input
              type="text"
              placeholder="Event Title"
              value={newEvent.title}
              onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
              className="border rounded-lg p-2"
              required
            />
            <input
              type="datetime-local"
              value={newEvent.date}
              onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
              className="border rounded-lg p-2"
              required
            />
            <textarea
              placeholder="Event Description"
              value={newEvent.description}
              onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              className="border rounded-lg p-2"
              rows="3"
            />
            <button
              type="submit"
              className={`text-white px-4 py-2 rounded-lg ${
                isEditing ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isEditing ? 'Update Event' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>

      {/* Events List */}
      <div className="space-y-4">
        {events.map(event => (
          <div key={event._id} className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{event.title}</h3>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <FaCalendarAlt />
                  <span>{new Date(event.date).toLocaleDateString()}</span>
                  <FaClock />
                  <span>{new Date(event.date).toLocaleTimeString()}</span>
                </div>
                <p className="text-gray-600">{event.description}</p>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleEdit(event)}
                  className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-full"
                >
                  <FaEdit size={20} />
                </button>
                <button 
                  onClick={() => handleDelete(event._id)}
                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-full"
                >
                  <FaTrash size={20} />
                </button>
              </div>
            </div>
            <div className="mt-4 text-sm font-medium text-purple-600">
              {calculateTimeLeft(event.date)}
            </div>
          </div>
        ))}
        
        {events.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <p>No events found. Create your first event!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Events;
